import api from "@/configs/axios-config";

export type MaterialSupplier = {
  supplier_id: number;
  supplier_name: string;
  supplier_code: string;
  material_type: Array<{
    material_type_id: number;
    material_type_name: string;
  }>;
};

export type MaterialSupplierDetail = {
  id: number;
  name: string;
  name_abbr: string;
  address_text: string;
  email: string;
  phones: Array<string>;
  is_active: boolean;
  material_type_ids: Array<{
    id: number;
    material_type_name: string;
    material_specs: Array<{
      material_spec_id: number;
      spec_name: string;
      base_price: number;
    }>;
  }>;
  formula: {
    formula_id: number;
    formula_name: string;
    formula_code: string;
    formula_category: string;
    formula: string;
    formula_js: string;
  };
  attributes: {
    commission_pct?: number;
    stock_pct?: number;
    credit_pct?: number;
    rounding?: "ROUND" | "ROUND UP" | "ROUND DOWN";
    rounding_digits?: number;
    usance?: number;
    interest?: number;
  };
  create_at: string;
  update_at: string;
  create_by: string;
  update_by: string;
};

export type MaterialSupplierForm = {
  name: string;
  name_abbr: string;
  address_text: string;
  email: string;
  phones: Array<string>;
  is_active: boolean;
  formula_id: number;
  attributes: {
    commission_pct?: number;
    stock_pct?: number;
    credit_pct?: number;
    rounding?: "ROUND" | "ROUND UP" | "ROUND DOWN";
    rounding_digits?: number;
    usance?: number;
    interest?: number;
  };
  material_type_ids: Array<number>;
};

export type MaterialSupplierPayload = {
  skip: number;
  limit: number;
  active_only: boolean;
  search?: string;
  material_type_id?: number;
};

export type Formular = {
  formula_id: number;
  formula_name: string;
  formula_code: string;
  formula_category: string;
  description?: string | null;
  has_commission_pct: boolean;
  has_stock_pct: boolean;
  has_credit_pct: boolean;
  has_rounding: boolean;
  has_loss1: boolean;
  has_loss2: boolean;
  has_loss1_pct: boolean;
  has_loss2_pct: boolean;
  has_usance: boolean;
  has_interest: boolean;
  has_rounding_digits: boolean;
  formula: string;
  formula_js: string;
  is_active?: boolean;
  created_at?: string;
  updated_at?: string;
  created_by?: string;
  updated_by?: string;
};

export const getMaterialSuppliers = async (
  payload: MaterialSupplierPayload,
): Promise<{ data: Array<MaterialSupplier>; total: number }> => {
  const response = await api.get(
    "/material-suppliers/list-with-material-types",
    {
      params: payload,
    },
  );
  return response.data;
};

export const getMaterialSupplierById = async (
  id?: number,
): Promise<MaterialSupplierDetail> => {
  const response = await api.get(`/material-suppliers/${id}`);
  return response.data;
};

export const createMaterialSupplier = async (
  payload: MaterialSupplierForm,
): Promise<void> => {
  const response = await api.post("/material-suppliers/", payload);
  return response.data;
};

export const updateMaterialSupplier = async (
  id: number,
  payload: MaterialSupplierForm,
): Promise<void> => {
  const response = await api.put(`/material-suppliers/${id}`, payload);
  return response.data;
};

export const getFormulars = async (): Promise<Array<Formular>> => {
  const response = await api.get("/material-suppliers/formulas", {
    params: { active_only: true },
  });
  return response.data?.data;
};

export type MaterialCode = {
  material_id: number;
  material_code: string;
  material_supplier_id: number;
  material_type_id: number;
  material_type_name: string;
  material_spec_id: number;
  material_spec_name: string | null;
  material_dimension_id: number;
  return_scrap_supplier_name: string | null;
  first_tier_supplier_id: number | null;
  first_tier_supplier_name: string | null;
  second_tier_supplier_id: number | null;
  second_tier_supplier_name: string | null;
  third_tier_suppliers_v2_id: number | null;
  ref_part_no: string;
  thickness: number;
  width: number;
  length_mm: number | null;
  extra_thickness_cost: number | null;
  cut_size_cost: number | null;
  packing_transportation_cost: number | null;
  loss1: number;
  is_loss1_pct: boolean;
  loss2: number;
  is_loss2_pct: boolean;
  commission_pct: number | null;
  stock_pct: number | null;
  credit_pct: number | null;
  rolling_cost: number | null;
  others_cost: number | null;
  thickness_deduction_const: number | null;
  material_price: number;
  others_cost_detail: Array<{
    id: number;
    cost: number;
  }>;
};

export type MaterialCodeDatail = {
  material_id: number;
  material_code: string;
  supplier_name: string;
  material_type_name: string;
  spec_name: string | null;
  dimension_name: string;
  return_scrap_supplier_name: string | null;
  first_tier_supplier_name: string | null;
  second_tier_supplier_name: string | null;
  thickness: number | null;
  width: number | null;
  length_mm: number | null;
  diameter: number | null;
  shaft_od: number | null;
  round_od: number | null;
  rectangle_x: number | null;
  rectangle_y: number | null;
}

export type MaterialColumn = {
  name: string;
  parameter: string;
};

export type MaterialCodeByDimension = {
  dimension_name: string;
  dimension_id: number;
  loss_type: string;
  dimension_columns: MaterialColumn[];
  formula_columns: MaterialColumn[];
  others_cost_column: {
    id: number;
    name: string;
  }[];
  data: MaterialCode[];
};

export type MaterialCodeByDimensionResponse = {
  dimensions: MaterialCodeByDimension[];
  total: number;
};

export type MaterialCodePayload = {
  skip: number;
  limit: number;
  material_supplier_id?: number;
  material_dimension_id?: number;
  is_active?: boolean;
  search?: string;
};

export type MaterialCodeTotalItem = {
  material_dimension_id: number;
  dimension_name: string;
  total: number;
};

export type CreateMaterialCodePayload = {
  material_id?: number;
  material_code: string;
  material_supplier_id: number;
  material_type_id: number;
  material_spec_id: number;
  material_dimension_id: number;
  first_tier_supplier_id: number;
  second_tier_supplier_id?: number;
  return_scrap_supplier_id?: number;
  ref_part_no: string;
  dimension_parameters: Record<string, number>;
  formula_parameters: Record<string, number | boolean>;
  others_cost_detail: Array<{
    id: number;
    cost: number;
  }>;
  is_active: boolean;
  formula_id: number;
  material_price: number;
  flagged_reason?: Set<string>;
};

export const getMaterialCodes = async (
  params: MaterialCodePayload,
): Promise<MaterialCodeByDimensionResponse> => {
  const response = await api.get("/materials/list", { params });
  return response.data;
};

export const getMaterialCodesTotal = async (params: {
  material_supplier_id?: number;
  is_active?: boolean;
}): Promise<MaterialCodeTotalItem[]> => {
  const response = await api.get("/materials/list-total", { params });
  return response.data;
};

export const getMaterialCodeById = async (
  id: number,
): Promise<MaterialCode> => {
  const response = await api.get(`/materials/${id}`);
  return response.data;
};

export const getMaterialDetailByPartId = async (
  partId: number,
  version: "v1" | "v2",
): Promise<MaterialCodeDatail[]> => {
  const response = await api.get(`/materials/by-part/${partId}`, {
    params: { version },
  });
  return response.data;
};

export type UpdateMaterialCodePayload = Partial<CreateMaterialCodePayload> & {
  material_id: number;
};

export const createMaterialCode = async (
  payload: CreateMaterialCodePayload,
): Promise<void> => {
  const response = await api.post("/materials/dynamic", payload);
  return response.data;
};

export const updateMaterialCode = async (
  payload: UpdateMaterialCodePayload[],
): Promise<void> => {
  const requestPayload = payload.map((item) => ({
    ...item,
    flagged_reason: item.flagged_reason
      ? Array.from(item.flagged_reason)
      : undefined,
  }));
  const response = await api.put(`/materials/dynamic/bulk`, requestPayload);
  return response.data;
};

export type OtherCostProcess = {
  id: number;
  column_name: string;
  display_order: number;
  is_active: boolean;
};

export type OtherCostProcessPayload = {
  material_supplier_id: number;
  column_name: string;
  display_order: number;
};

export const getOtherCostProcesses = async (
  material_supplier_id: number,
): Promise<OtherCostProcess[]> => {
  const response = await api.get(
    `/materials/others-cost-columns/${material_supplier_id}`,
  );
  return response.data;
};

export const createOtherCostProcess = async (
  otherCostProcessPayload: OtherCostProcessPayload,
) => {
  const response = await api.post(
    `/materials/others-cost-columns`,
    otherCostProcessPayload,
  );
  return response.data;
};

export const updateOtherCostProcess = async (
  otherCostProcessPayload: OtherCostProcessPayload,
  column_id: number,
) => {
  const response = await api.put(
    `/materials/others-cost-columns/${otherCostProcessPayload.material_supplier_id}/${column_id}`,
    {
      column_name: otherCostProcessPayload.column_name,
      display_order: otherCostProcessPayload.display_order,
    },
  );
  return response.data;
};

export const deleteOtherCostProcess = async (
  material_supplier_id: number,
  column_id: number,
) => {
  const response = await api.delete(
    `/materials/others-cost-columns/${material_supplier_id}/${column_id}`,
  );
  return response.data;
};

export type SimpleMaterialCode = {
  material_id: number;
  material_code: string;
  material_price: number;
  volume: number;
  return_scrap_price: number;
  material_supplier_id?: number;
  material_spec_id?: number;
  material_dimension_id?: number;
  second_tier_supplier_id?: number | null;
  return_scrap_supplier_id?: number | null;
  thickness?: number;
  width?: number;
  length_mm?: number | null;
  diameter?: number | null;
  round_od?: number | null;
  shaft_od?: number | null;
  rectangle_x?: number | null;
  rectangle_y?: number | null;
};

export const getMaterialCodeByFirstTierSupplier = async (
  supplierCode: string,
): Promise<SimpleMaterialCode[]> => {
  const response = await api.get(
    `/materials/list/first-tier/${supplierCode}`,
  );
  return response.data?.data;
};
